import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouteMatch } from 'react-router';
import OuterContainer from '../PatientSummary/OuterContainer';
import { styled } from '@mui/system';
import { LabPanelType } from './Interfaces';
import ResultsTable from './ResultsTable';
import DetailTable from './DetailTable';
import { Card } from 'shared-components/components';
import { getRemSize } from 'shared-components/StyledComponents/functions';
import dayjs from 'dayjs';
import { GET_PATIENT_LAB_PANELS } from '../PatientSummary/PatientProfileQueries';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { ROPatientContextProvider } from '../PatientSummary/context';
import { LARGE_DISPLAY_SIZE } from 'shared-components/enums';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { FindInPageOutlined as FindInPageOutlinedIcon } from '@mui/icons-material';

const ResultsContainer = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const DetailContainer = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    min-height: 400px;
  }
`;

const DetailContent = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const DetailHeader = styled(Stack)`
  flex-shrink: 0;
`;

const Date = styled('span')`
  font-size: ${getRemSize(16)};
`;

const NoDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 50px;
`;

const NO_DATA = 'No results to show';

interface ROPatientLabsNavType {
  id: string;
}

const ROPatientLabs = (): JSX.Element => {
  const windowSize = useWindowSize();
  const [activeResult, setActiveResult] = useState<LabPanelType | undefined>();
  const [activeDocument, setActiveDocument] = useState<string | undefined>();
  const isSmallDevice = windowSize.width < LARGE_DISPLAY_SIZE;
  const match = useRouteMatch<ROPatientLabsNavType>();
  const { id } = match.params;
  const { data, loading } = useQuery(GET_PATIENT_LAB_PANELS, {
    variables: { patientId: id },
  });
  const theme = useTheme();

  if (loading) return <>Loading...</>;

  return (
    <ROPatientContextProvider>
      <OuterContainer>
        <Grid container sx={{ width: '100%', paddingX: '16px', height: '100%', display: 'flex' }}>
          <Grid xs={isSmallDevice ? 4 : 3} sx={{ height: '100%', minHeight: '400px' }}>
            <Stack height="100%">
              <ResultsContainer>
                <Typography paddingBottom={1} variant="h6" id="patient-summary-header">
                  Lab Results
                </Typography>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <ResultsTable
                    data={data.labPanels}
                    setActiveResult={setActiveResult}
                    setActiveDocument={setActiveDocument}
                  />
                </div>
              </ResultsContainer>
            </Stack>
          </Grid>
          <Grid xs={isSmallDevice ? 8 : 9} sx={{ paddingLeft: '16px', height: '100%' }}>
            <DetailContainer $filled>
              {activeResult && activeResult.observations?.length ? (
                <DetailContent>
                  <DetailHeader paddingBottom={1} alignItems="center" justifyContent="space-between" direction="row">
                    <Stack data-testid="labs-results-table-header" direction="row" gap={0.5}>
                      <Typography variant="h6" data-test-id="document-preview-header">
                        {activeResult.panelName || 'No title available'}
                      </Typography>
                      <Typography variant="h6" color={theme.palette.text.secondary}>
                        ({activeResult.status})
                      </Typography>
                    </Stack>
                    <Date>{dayjs(activeResult.observationDate).utc().format('D MMMM YYYY h:mm:ss A')}</Date>
                  </DetailHeader>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <DetailTable
                      testId={'labs-results-table'}
                      activeResult={activeResult}
                      activeDocument={activeDocument}
                      setActiveDocument={setActiveDocument}
                    />
                  </div>
                </DetailContent>
              ) : (
                <NoDataContainer>
                  <FindInPageOutlinedIcon fontSize="large" />
                  <Typography paddingTop={2} variant="body1">
                    {NO_DATA}
                  </Typography>
                </NoDataContainer>
              )}
            </DetailContainer>
          </Grid>
        </Grid>
      </OuterContainer>
    </ROPatientContextProvider>
  );
};

export default ROPatientLabs;
